import { useTranslation } from "react-i18next";
import {
  NumberField,
  SettingRow,
  SettingSwitch,
  SettingsCard,
} from "@/features/changelog/components/shared/SettingsCard";
import { AUTO_REFRESH_DEFAULT_SECONDS, LIMITS } from "./ops-settings-limits";
import { SlackWarning } from "./SlackWarning";
import type { OpsSettingsCardProps } from "./types";

/**
 * How the Cloud Scheduler tab reads job health, whether it self-refreshes, and
 * which job Slack alerts are armed. Display knobs never change cadences; the
 * alert toggles only gate messages (R19).
 */
export function ScheduledJobsSettingsCard({
  draft,
  set,
  isReadOnly,
  isSlackConfigured,
}: OpsSettingsCardProps) {
  const { t } = useTranslation();
  const autoRefreshOn = draft.jobsAutoRefreshSeconds > 0;
  const anyJobAlertOn =
    draft.alertOnJobFailures || draft.alertOnJobMissed || draft.alertOnJobLifecycle;

  return (
    <SettingsCard
      icon="clock"
      title={t("admin.opsSettings.jobs.title", "Scheduled jobs")}
      description={t(
        "admin.opsSettings.jobs.description",
        "How the Cloud Scheduler tab reads job health, whether it keeps itself fresh, and which job Slack alerts are on.",
      )}
    >
      <SettingRow
        htmlFor="jobs-overdue-grace"
        label={t("admin.opsSettings.overdueGrace", "Flag a job as overdue after")}
        description={t(
          "admin.opsSettings.overdueGraceHelp",
          "Slack given past a job's own next-run time before the Overdue badge shows, so ordinary jitter does not raise flags.",
        )}
        control={
          <NumberField
            id="jobs-overdue-grace"
            value={draft.jobsOverdueGraceMinutes}
            min={LIMITS.jobsOverdueGraceMinutes.min}
            max={LIMITS.jobsOverdueGraceMinutes.max}
            unit={t("admin.opsSettings.minutes", "minutes")}
            disabled={isReadOnly}
            onChange={(value) => set("jobsOverdueGraceMinutes", value)}
          />
        }
      />

      <SettingRow
        htmlFor="jobs-unhealthy-after"
        label={t("admin.opsSettings.unhealthyAfter", "Turn the badge red after")}
        description={t(
          "admin.opsSettings.unhealthyAfterHelp",
          "A shorter streak shows as an amber warning instead, so one flaky run does not read as an outage.",
        )}
        control={
          <NumberField
            id="jobs-unhealthy-after"
            value={draft.jobsUnhealthyAfterFailures}
            min={LIMITS.jobsUnhealthyAfterFailures.min}
            max={LIMITS.jobsUnhealthyAfterFailures.max}
            unit={t("admin.opsSettings.failuresInARow", "failures in a row")}
            disabled={isReadOnly}
            onChange={(value) => set("jobsUnhealthyAfterFailures", value)}
          />
        }
      />

      <SettingRow
        htmlFor="jobs-auto-refresh"
        label={t("admin.opsSettings.autoRefresh", "Refresh the list automatically")}
        description={t(
          "admin.opsSettings.autoRefreshHelp",
          "Useful on a wall screen. Off, the tab refreshes when opened or when you press Refresh.",
        )}
        control={
          <SettingSwitch
            id="jobs-auto-refresh"
            checked={autoRefreshOn}
            disabled={isReadOnly}
            onCheckedChange={(checked) =>
              set("jobsAutoRefreshSeconds", checked ? AUTO_REFRESH_DEFAULT_SECONDS : 0)
            }
          />
        }
      />

      <SettingRow
        htmlFor="jobs-auto-refresh-seconds"
        isNested
        isMuted={!autoRefreshOn}
        label={t("admin.opsSettings.autoRefreshEvery", "Refresh every")}
        description={t(
          "admin.opsSettings.autoRefreshEveryHelp",
          "Cadences run in hours, so a minute is usually fresh enough.",
        )}
        control={
          <NumberField
            id="jobs-auto-refresh-seconds"
            value={autoRefreshOn ? draft.jobsAutoRefreshSeconds : AUTO_REFRESH_DEFAULT_SECONDS}
            min={LIMITS.jobsAutoRefreshSeconds.min}
            max={LIMITS.jobsAutoRefreshSeconds.max}
            unit={t("admin.opsSettings.seconds", "seconds")}
            disabled={isReadOnly || !autoRefreshOn}
            onChange={(value) => set("jobsAutoRefreshSeconds", value)}
          />
        }
      />

      <SettingRow
        htmlFor="jobs-alert-failures"
        label={t("admin.opsSettings.jobFailureAlert", "Post to Slack when a job fails")}
        description={t(
          "admin.opsSettings.jobFailureAlertHelp",
          "When the failure streak hits the red badge threshold. One message per bad stretch, not one per retry.",
        )}
        control={
          <SettingSwitch
            id="jobs-alert-failures"
            checked={draft.alertOnJobFailures}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("alertOnJobFailures", checked)}
          />
        }
      />

      <SettingRow
        htmlFor="jobs-alert-missed"
        label={t("admin.opsSettings.jobMissedAlert", "Post to Slack when a job is missed")}
        description={t(
          "admin.opsSettings.jobMissedAlertHelp",
          "When an enabled job stays past its next-run time longer than the overdue grace above.",
        )}
        control={
          <SettingSwitch
            id="jobs-alert-missed"
            checked={draft.alertOnJobMissed}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("alertOnJobMissed", checked)}
          />
        }
      />

      <SettingRow
        htmlFor="jobs-alert-lifecycle"
        label={t("admin.opsSettings.jobLifecycleAlert", "Post to Slack on job lifecycle changes")}
        description={t(
          "admin.opsSettings.jobLifecycleAlertHelp",
          "Create, pause, resume, delete, and Run now. Off by default — that chatter is usually noise.",
        )}
        control={
          <SettingSwitch
            id="jobs-alert-lifecycle"
            checked={draft.alertOnJobLifecycle}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("alertOnJobLifecycle", checked)}
          />
        }
      />

      {anyJobAlertOn && !isSlackConfigured ? <SlackWarning /> : null}
    </SettingsCard>
  );
}
